import { useCallback, useMemo } from 'react'

import { FormControlLabel, Radio, RadioGroup } from '@mui/material'

import { useTexts } from '../../../hooks/useTexts'
import { createValidateCourierFilterDrivers } from '../../../hooks/validations/createValidateCourierFilterDrivers'

import { form } from '../../../formState'

import { DriversRestrictionValue } from '../../../types'

export type Option = string[]

type Props = {
  territoryId: string
  territoriesData: uui.domain.client.rm.TerritoriesDriversAndVehicles
}

export function DriversAccess(props: Props) {
  const { territoryId, territoriesData } = props
  const texts = useTexts()
  const { api, values } = form.useForm()

  const type = form.useFormField('type')

  const driverIdFieldRestrictionValue = values.drivers?.[territoryId]?.restriction
  const restriction = driverIdFieldRestrictionValue || 'not-restricted'

  const items = useMemo(() => {
    return Object.keys(territoriesData[territoryId]?.drivers ?? {})
  }, [territoriesData, territoryId])

  const onRadioChange = useCallback(
    (value: DriversRestrictionValue) => {
      api.change(`drivers.${territoryId}.ids`, [])
      api.change(`drivers.${territoryId}.restriction`, value)
    },
    [api, territoryId],
  )

  const getOptionLabel = useCallback(
    (option: string) => {
      return territoriesData[territoryId]?.drivers?.[option] ?? ''
    },
    [territoriesData, territoryId],
  )

  return (
    <form.Fieldset spacing={0.5}>
      <form.SectionSubtitle>{texts.labels.drivers.title}</form.SectionSubtitle>

      <RadioGroup
        name={`drivers.${territoryId}.restriction`}
        value={restriction}
        onChange={e => onRadioChange(e.target.value as DriversRestrictionValue)}
      >
        <FormControlLabel
          value="not-restricted"
          control={<Radio />}
          label={texts.labels.notRestricted}
        />
        <FormControlLabel value="restricted" control={<Radio />} label={texts.labels.restricted} />
      </RadioGroup>

      {restriction === 'restricted' && (
        <form.MultiSelect
          key={territoryId}
          name={`drivers.${territoryId}.ids`}
          validateOn="blur change"
          onValidate={(field: { value: Option[] }) => {
            const validationCourierFilterDrivers = createValidateCourierFilterDrivers(
              texts,
              field.value ?? ([] as unknown as Option[]),
              type.field.value,
              restriction,
            )

            return validationCourierFilterDrivers?.courierFilterDriversErrors?.message
          }}
          multiSelectProps={{
            label: texts.labels.drivers.fieldLabel,
            items: items ?? [],
            getLabel: getOptionLabel,
            getOptionDisabled: c => !c,
            inputMode: 'normal',
            optionMode: 'normal',
          }}
        />
      )}
    </form.Fieldset>
  )
}
